import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const OPENAI_MODELS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (rápido e barato)" },
  { value: "gpt-4o", label: "GPT-4o (mais capaz)" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (mais barato)" },
];

const useStyles = makeStyles((theme) => ({
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  field: { marginBottom: theme.spacing(2) },
}));

const initialState = {
  name: "",
  apiKey: "",
  model: "gpt-4o-mini",
  provider: "openai",
};

const AiCredentialModal = ({ open, onClose, onSaved, credential }) => {
  const classes = useStyles();
  const [form, setForm] = useState(initialState);
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (credential) {
      setForm({
        name: credential.name || "",
        apiKey: credential.apiKey || "",
        model: credential.model || "gpt-4o-mini",
        provider: credential.provider || "openai",
      });
    } else {
      setForm(initialState);
    }
  }, [credential, open]);

  const handleChange = (e) => {
    const { name, value } = e.target; // captura antes do event pool ser limpo pelo React
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Informe um nome para a credencial");
    if (!form.apiKey.trim()) return toast.error("Informe a API Key");
    if (!form.model) return toast.error("Selecione o modelo");

    setSaving(true);
    try {
      if (credential?.id) {
        await api.put(`/ai-credentials/${credential.id}`, form);
        toast.success("Credencial atualizada!");
      } else {
        await api.post("/ai-credentials", form);
        toast.success("Credencial criada!");
      }
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {credential ? "Editar Credencial de IA" : "Nova Credencial de IA"}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          className={classes.field}
          label="Nome da credencial"
          name="name"
          value={form.name}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          size="small"
          placeholder="Ex: OpenAI Produção"
          required
        />

        <TextField
          className={classes.field}
          label="API Key"
          name="apiKey"
          value={form.apiKey}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          size="small"
          type={showApiKey ? "text" : "password"}
          placeholder="sk-..."
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowApiKey((v) => !v)}>
                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant="outlined" fullWidth size="small" className={classes.field}>
          <InputLabel>Modelo</InputLabel>
          <Select
            name="model"
            value={form.model}
            onChange={handleChange}
            label="Modelo"
          >
            {OPENAI_MODELS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Provedor fixo em OpenAI por enquanto */}
        <TextField
          label="Provedor"
          value="OpenAI"
          variant="outlined"
          fullWidth
          size="small"
          disabled
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
          className={classes.btnWrapper}
        >
          {saving ? (
            <CircularProgress size={24} className={classes.buttonProgress} />
          ) : credential ? (
            "Salvar"
          ) : (
            "Criar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiCredentialModal;
